/**
 * 内联新增维度：确认时字段校验错误状态（对象形式，便于扩展多字段）。
 *
 * 与表格/区块校验分离：仅在「确认添加」时写入 error；
 * 输入过程中不主动高亮，修正后 `clearInlineFieldError` 清除单项。
 */
import { useCallback, useEffect, useState } from 'react';

import { isDimSampleValuesFormatValid } from '../../../utils/fieldConfigRowUtils';
import type { IFieldConfigRowEdit } from '../../../types';
import type { TDimInlineFieldErrorKey, TDimInlineFieldErrors } from '../types';

const EMPTY_DIM_INLINE_FIELD_ERRORS: TDimInlineFieldErrors = {};

const hasDimInlineFieldErrors = (errors: TDimInlineFieldErrors): boolean =>
  Object.values(errors).some(Boolean);

/**
 * 点击「确认添加」时执行；
 * 返回需高亮的字段 key（当前仅 `sampleValues` JSON 数组格式）。
 */
const validateDimInlineAddOnConfirm = (
  draft: IFieldConfigRowEdit,
): TDimInlineFieldErrors => {
  const errors: TDimInlineFieldErrors = {};

  if (
    draft.sampleValues.trim() &&
    !isDimSampleValuesFormatValid(draft.sampleValues)
  ) {
    errors.sampleValues = true;
  }

  return errors;
};

interface IUseDimInlineFieldErrorsResult {
  inlineFieldErrors: TDimInlineFieldErrors;
  isInlineFieldError: (key: TDimInlineFieldErrorKey) => boolean;
  clearInlineFieldError: (key: TDimInlineFieldErrorKey) => void;
  validateOnConfirm: (draft: IFieldConfigRowEdit) => boolean;
}

export const useDimInlineFieldErrors = (
  dimInlineOpen: boolean,
): IUseDimInlineFieldErrorsResult => {
  const [inlineFieldErrors, setInlineFieldErrors] =
    useState<TDimInlineFieldErrors>(EMPTY_DIM_INLINE_FIELD_ERRORS);

  useEffect(() => {
    if (!dimInlineOpen) {
      setInlineFieldErrors(EMPTY_DIM_INLINE_FIELD_ERRORS);
    }
  }, [dimInlineOpen]);

  const isInlineFieldError = useCallback(
    (key: TDimInlineFieldErrorKey): boolean => Boolean(inlineFieldErrors[key]),
    [inlineFieldErrors],
  );

  const clearInlineFieldError = useCallback(
    (key: TDimInlineFieldErrorKey): void => {
      setInlineFieldErrors((prev) => {
        if (!prev[key]) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const validateOnConfirm = useCallback((draft: IFieldConfigRowEdit): boolean => {
    const errors = validateDimInlineAddOnConfirm(draft);
    if (hasDimInlineFieldErrors(errors)) {
      setInlineFieldErrors(errors);
      return false;
    }
    setInlineFieldErrors(EMPTY_DIM_INLINE_FIELD_ERRORS);
    return true;
  }, []);

  return {
    inlineFieldErrors,
    isInlineFieldError,
    clearInlineFieldError,
    validateOnConfirm,
  };
};
